import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios defaults — use Bearer token only, no cookies needed
axios.defaults.withCredentials = false;

// Axios interceptor to add token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function getErrorMessage(e) {
  // 1. Check for structured API error detail
  const detail = e.response?.data?.detail;
  if (detail) {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((item) => (item?.msg || JSON.stringify(item))).join(". ");
    if (typeof detail === "object" && detail.msg) return detail.msg;
    return String(detail);
  }
  // 2. Check for other response data shapes
  if (e.response?.data?.message) return e.response.data.message;
  if (e.response?.data?.error) return e.response.data.error;
  // 3. HTTP status based messages
  if (e.response?.status === 400) return "Invalid request. Please check your input.";
  if (e.response?.status === 409) return "This email is already registered.";
  if (e.response?.status === 422) return "Please fill in all fields correctly.";
  if (e.response?.status === 429) return "Too many attempts. Please wait and try again.";
  if (e.response?.status === 500) return "Server error. Please try again in a moment.";
  // 4. Network errors
  if (e.code === "ERR_NETWORK") return "Network error. Please check your connection.";
  if (e.message) return e.message;
  return "Something went wrong. Please try again.";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = not auth, object = auth
  const checkedRef = useRef(false);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      localStorage.removeItem("auth_token");
      setUser(null);
    }
  }, []);

  useEffect(() => {
    if (!checkedRef.current) {
      checkedRef.current = true;
      checkAuth();
    }
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.token) {
        localStorage.setItem("auth_token", data.token);
      }
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      return { success: true };
    } catch (e) {
      return { success: false, error: getErrorMessage(e) };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { name, email, password });
      if (data.token) {
        localStorage.setItem("auth_token", data.token);
      }
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      return { success: true };
    } catch (e) {
      return { success: false, error: getErrorMessage(e) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch { /* ignore */ }
    localStorage.removeItem("auth_token");
    setUser(null);
  };

  const loading = user === undefined;

  return (
    <AuthContext.Provider value={{ user: loading ? null : user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
